import React from "react";
import {
  isEmpty, TextInput,
  useDataProvider,
  useNotify,
} from "react-admin";
import {
  emailWithSpacesValidation,
  maxLengthValidation,
  requiredValidation,
  validateEmailList
} from "../../utils/util";
import {canFieldBeHidden, filterFieldBasedOnName, extractFirstLevelUrlParams, resolveFirstLevelFieldDefault} from "../../utils/CreateReportUtils";
import TextInputWithCharCount from "../common/TextInputWithCharCount";
import Divider from "@mui/material/Divider";
import ReportTypeAndDynamicTimeRange from "./ReportTypeAndDateRangeForCreateReportGrid";
import IncomeDynamicFields from "./IncomeVerificationForCreateReportGrid";
import CurrencyAndAmountDynamicFields from "./CurrencyAndATPForCreateReportGrid";
import SurveyDynamicFields from "./SurveyForCreateReportGrid";
import SurveyATPDynamicField from "./SurveyATPForCreateReportGrid";
import InsightsDynamicField from "./InsightsForCreateReportGrid";
import {LargeItemDynamicInput} from "./LargeItemDynamicInput";
import {Grid} from "@mui/material";
import ListItem from "@mui/material/ListItem";
import {display, padding2, textColor, width} from "../Styles";
import { PersonaTemplateComponent } from "./PersonaTemplate";
import { useFormContext } from "react-hook-form";
import { REPORT_GRID_COMPONENT_POSITIONS } from "../Constants";
import { isIncomeInsightsEnabled } from "../DataProvider";
import { useLocation } from "react-router";

export const CreateElementsPrimaryReportGrid = (props) => {
  const [clientSurveys, setClientSurveys] = React.useState([]);
  const [clientSurveysError, setClientSurveysError] = React.useState();
  const dataProvider = useDataProvider();
  const [personaTemplates, setPersonaTemplates] = React.useState([]);
  const notify = useNotify();
  const form = useFormContext();
  const location = useLocation();

  const { isMatterShow } = props;
  const urlParams = extractFirstLevelUrlParams(location?.search);
  const context = { isMatterShow, matterName: props.matterName, userEmail: localStorage.getItem('userEmail') };

  const firstLevelFields = props.fields.filter(field => isEmpty(field.dependentOn)).sort((a, b) => a.position - b.position);
  let components = [];
  const clientId = localStorage.getItem("clientId");
  const reportTypeField = filterFieldBasedOnName("reportTypes", props.fields)
  const frequencyField = filterFieldBasedOnName("frequency", props.fields)
  const timeRangeField = filterFieldBasedOnName("timeRange", props.fields)
  const currencyField = filterFieldBasedOnName("amountCurrency", props.fields)
  const amountField = filterFieldBasedOnName("amountToProve", props.fields)
  const surveyAmountField = filterFieldBasedOnName("surveyAmountToProve", props.fields)
  const sourceOfFundsSurveyField = filterFieldBasedOnName("sourceOfFundsSurvey", props.fields)
  const factFindSurveyField = filterFieldBasedOnName("factFindSurvey", props.fields)
  const insightsField = filterFieldBasedOnName("insights", props.fields)
  const incomeMandatoryField = filterFieldBasedOnName("incomeMandatory", props.fields)
  const payslipVerificationField = filterFieldBasedOnName("payslipVerification", props.fields)
  const followUpDataRangeField = filterFieldBasedOnName("followUpDataRange", props.fields)
  const largeItemField = filterFieldBasedOnName("largeItem", props.fields)
  const personaTemplateField = filterFieldBasedOnName("identityVerificationTemplate", props.fields)
  const emailAddressField = filterFieldBasedOnName("emailAddress", props.fields)
  const fromField = filterFieldBasedOnName("from", props.fields);
  const toField = filterFieldBasedOnName("to", props.fields);
  const birthDateField = filterFieldBasedOnName("birthDate", props.fields);
  const countryField = filterFieldBasedOnName("country", props.fields);
  const multiReportField = filterFieldBasedOnName("multiReportType", props.fields);  

  React.useEffect(() => {
    async function getSurveys() {
      dataProvider
        .getList('survey')
        .then(async ({ data }) => {
          setClientSurveys(data);
        })
        .catch((error) => {
          setClientSurveysError(error);
        });
    }
    getSurveys()
  }, [dataProvider]); 
  
  React.useEffect(() => {
    function getPersonaTemplates() {
      dataProvider
        .getPersonaTemplatesForClient({ clientId })
        .then(({ data }) => {
          setPersonaTemplates(data);
        })
        .catch((error) => {
          notify("Something went wrong while fetching identity templates", { type: 'warning', autoHideDuration: 2000 });
        });
    }
    getPersonaTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {//crs-hook
    const notificationRecipientsField = filterFieldBasedOnName("notificationRecipients", props.fields);    

    form.resetField("businessName");
    form.resetField("descriptionOfMatter");
    if(!isMatterShow && !isEmpty(notificationRecipientsField?.defaultValue)){
      form.setValue("notificationRecipients", notificationRecipientsField?.defaultValue)
    }else{
      form.setValue("notificationRecipients", undefined);
    }    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedTemplateId, props.fields]);
  
  if (props.isMatterShow) {
    components.push(
      <div style={display.none}>
        <TextInput source="matterId" name="matterId" defaultValue={props.matterId} />
        <TextInput source="accounts" name="accounts" defaultValue={props.accounts} />
      </div>
    );
  } else {
    components.push(
      <div style={display.none}>
        <TextInput source="callbackUrl" name="callbackUrl" defaultValue={urlParams.callbackUrl} />
      </div>
    );
  }

  firstLevelFields.forEach((field) => {
    const resolvedDefault = resolveFirstLevelFieldDefault(field, urlParams, context);

    if (field.type === 'Textfield') {
      const item = (
        <ListItemComponentForReport
          source={field.name}
          title={field.displayName}
          name={field.name}
          required={field.required}
          label={field.placeholder}
          multiline={field.multiline}
          defaultValue={resolvedDefault}
          hidden={field.isHidden}
          isMatterShow={props.isMatterShow}
          validate={[
            field.required && requiredValidation,
            field.name === 'emailAddress' && emailWithSpacesValidation
          ]}
        />
      );
      components.push(item);
    } else if (field.type === 'EmailArrayField') {
      const item = (
        <ListItemComponentForReport
          source={field.name}
          title={field.displayName}
          name={field.name}
          required={field.required}
          label={field.placeholder}
          multiline={field.multiline}
          defaultValue={resolvedDefault}
          hidden={field.isHidden}
          isMatterShow={isMatterShow}
          validate={[
            field.required && requiredValidation,
            validateEmailList,
          ]}
        />
      );
      if(!(isMatterShow && field.name === 'notificationRecipients')){
        components.push(item);
      }
    } else if (field.type === 'TextfieldWithCharCount') {
      if (!(props.isMatterShow && field.name === 'emailText')) {
        const item = (
          <React.Fragment>
            <Divider light />
            <TextInputWithCharCount
              title={field.displayName}
              source={field.name}
              required={field.required}
              name={field.name}
              label={field.placeholder}
              multiline={true}
              hidden={field.isHidden}
              validate={[field.required && requiredValidation, maxLengthValidation(250)]}
              defaultValue={resolvedDefault}
              maxLength={250}
            />
          </React.Fragment>
        );
        components.push(item);
      }
    } else if (field.type === 'Dropdown' && (field.name === 'reportTypes' || field.name === 'multiReportType')) {
      
      const reportTypeAndDateRangeInput = (
        <ReportTypeAndDynamicTimeRange
          reportField={reportTypeField}
          frequencyField={frequencyField}
          timeRangeField={timeRangeField}
          selectedTemplateId={props.selectedTemplateId}
          isMatterShow={props.isMatterShow}
          startDate={props.startDate}
          endDate={props.endDate}
          fromField={fromField}
          toField={toField}
          multiReportField={multiReportField}
          isBundleTemplate={props.isBundleTemplate}
        />
      )

      const incomeInput = (!props.isMatterShow &&
        <IncomeDynamicFields
          selectedTemplateId={props.selectedTemplateId}
          payslipVerificationField={payslipVerificationField}
          followUpDataRangeField={followUpDataRangeField}
        />
      )

      const currencyAndAmountInput = (
        <CurrencyAndAmountDynamicFields currencyField={currencyField} amountField={amountField} selectedTemplateId={props.selectedTemplateId} isMatterShow={props.isMatterShow} />        
      )

      const surveyInput = ( !props.isMatterShow &&
        <>
        <SurveyDynamicFields
          sourceOfFundsSurveyField={sourceOfFundsSurveyField}
          factFindSurveyField={factFindSurveyField}
          selectedTemplateId={props.selectedTemplateId}
          clientSurveys={clientSurveys}
          clientSurveysError={clientSurveysError}
        />
        <SurveyATPDynamicField surveyAmountField={surveyAmountField} selectedTemplateId={props.selectedTemplateId} />
        </>
      );

      const insightsInput = ( (!props.isMatterShow && isIncomeInsightsEnabled()) &&
        <InsightsDynamicField
          selectedTemplateId={props.selectedTemplateId}
          insightsField={insightsField}
          incomeMandatoryField={incomeMandatoryField}
        />
      );

      const largeItemInput = ( !props.isMatterShow &&
        <LargeItemDynamicInput largeItemField={largeItemField} selectedTemplateId={props.selectedTemplateId} />
      )

      const identityInput = ( !props.isMatterShow && !isEmpty(personaTemplates) &&
        <PersonaTemplateComponent 
          personaTemplateField={personaTemplateField}
          emailAddressField={emailAddressField}
          birthDateField={birthDateField}
          countryField={countryField}
          phoneNumberField={filterFieldBasedOnName("identityPhoneNumber", props.fields)}
          addressStreet1Field={filterFieldBasedOnName("identityAddressStreet1", props.fields)}
          addressStreet2Field={filterFieldBasedOnName("identityAddressStreet2", props.fields)}
          addressCityField={filterFieldBasedOnName("identityAddressCity", props.fields)}
          addressSubdivisionField={filterFieldBasedOnName("identityAddressSubdivision", props.fields)}
          addressPostalCodeField={filterFieldBasedOnName("identityAddressPostalCode", props.fields)}
          personaTemplates={personaTemplates} 
          selectedTemplateId={props.selectedTemplateId}                   
        />
      )      
      
      const componentEntries = [
        {
          position : reportTypeField?.position ?? multiReportField.position ?? REPORT_GRID_COMPONENT_POSITIONS.REPORT_TYPE_AND_DATE_RANGE,
          components : reportTypeAndDateRangeInput,
        },
        {
          position : followUpDataRangeField?.position ?? REPORT_GRID_COMPONENT_POSITIONS.INCOME,
          components : incomeInput,
        },
        {
          position : currencyField?.position ?? REPORT_GRID_COMPONENT_POSITIONS.CURRENCY_AND_AMOUNT,
          components : currencyAndAmountInput,
        },
        {
          position : factFindSurveyField?.position ?? sourceOfFundsSurveyField?.position ?? REPORT_GRID_COMPONENT_POSITIONS.SURVEY,
          components : surveyInput,
        },
        {
          position : insightsField?.position ?? REPORT_GRID_COMPONENT_POSITIONS.INSIGHTS,
          components : insightsInput,
        },
        {
          position : largeItemField?.position ?? REPORT_GRID_COMPONENT_POSITIONS.LARGE_ITEM,
          components : largeItemInput,
        },
        {
          position : personaTemplateField?.position ?? REPORT_GRID_COMPONENT_POSITIONS.IDENTITY,
          components : identityInput,
        }
      ];
      
      const otherComponents = props.isBundleTemplate
      ? componentEntries.sort((a, b) => a.position - b.position).map((item) => item.components)
      : componentEntries.map((item) => item.components);

      components = [...components, ...otherComponents];
    }
  });
            
  return components;
};

const ListItemComponentForReport = (props) => {
  return (
    <React.Fragment>
      <Grid sx={{
        ...canFieldBeHidden(
          props.hidden,
          props.required,
          props.defaultValue
        ) ? { display: "none" } : {}
      }}>
        <ListItem sx={padding2.y}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={4} sx={textColor.gray}>
              {props.title}
              {props.required && <span style={{ ...textColor.red, marginLeft: "3px" }}>*</span>}
            </Grid>
            <Grid item xs={12} md={props.isMatterShow ? 8 : 6} lg={props.isMatterShow ? 8 : 5}>
              <TextInput
                source={props.source}
                name={props.name}
                label={props.label}
                variant="outlined"
                size="small"
                sx={{ ...width.fluid.w100, marginBottom: "-15px" }}
                multiline={props.multiline}
                defaultValue={props.defaultValue}
                validate={props.validate}
                key={props.source}
              />
            </Grid>
          </Grid>
        </ListItem>
        <Divider light />
      </Grid>
    </React.Fragment>
  );
};